<template>
    <BaseModal
        v-if="visible"
        ModalId="modalId"
        :HideModalFun="onHidden"
        :TitleWindow="title"
        :width="'50%'"
        height="auto"
        :buttons="[
            { text: $t('Shared.BtnCancel'), variant: 'secondary', action: 'Close' },
            {
                text: $t('Shared.Save'),
                variant: 'primary',
                action: emitEventOK,
                disabled: saveButtonDisabled
            }
        ]"
        @Close="close"
    >
        <b-row>
            <b-col lg="4" md="4" sm="12">
                <MdDateTime
                    :Label="$t('Shared.datefrom')"
                    :Model="parameters.dateFrom"
                    @input="dateFromChanged($event)"
                    :Id="'payment-date-from'"
                    :Type="'date'"
                    :isConfirm="false"
                    :ReadOnly="searching"
                    :isValid="isDateFromValid"
                >
                </MdDateTime>
            </b-col>
            <b-col lg="4" md="4" sm="12">
                <MdDateTime
                    :Label="$t('Shared.dateTo')"
                    :Model="parameters.dateTo"
                    @input="dateToChanged($event)"
                    :Id="'payment-date-to'"
                    :Type="'date'"
                    :isConfirm="false"
                    :ReadOnly="searching"
                    :isValid="isDateToValid"
                >
                </MdDateTime>
            </b-col>
            <b-col lg="4" md="4" sm="12">
                <MdButton
                    v-bind:Label="$t('Shared.Search')"
                    v-bind:Name="'btnSearchPatient'"
                    v-bind:ClassProp="'secondary mx-2 btn-login pull-right mt-3'"
                    v-on:click="searchAsync"
                    :isReadOnly="searching"
                />
            </b-col>
        </b-row>

        <MdTitle
            v-bind:Text="$t('Shared.SearchResult')"
            v-bind:isSubtitle="true"
            v-bind:Name="'T_TitleSearch'"
        />

        <b-row>
            <b-col lg="12" md="12" sm="12">
                <MdTable
                    v-bind:fields="fields"
                    v-bind:items="items"
                    v-bind:totalRows="totalRows"
                    v-bind:showMessageIsEmpty="showMessageIsEmpty"
                    v-bind:currentPage="currentPage"
                    v-bind:pageOptions="pageOptions"
                    v-bind:refTable="'patientSelectionClaim'"
                    v-bind:perPage="perPage"
                    :onFiltered="onFiltered"
                    :pageChanged="pageChanged"
                    :perPageFunction="PerPageFunction"
                    :ShowSelectAll="true"
                    :ShowSelectedCount="true"
                    :ShowSearchCriteria="true"
                    :multiSelect="true"
                    :SelectionKey="'patientCode'"
                    :SelectAllFunction="getAllPatientsForSelectAll"
                    :onSearchCriteria="onSearchCriteriaChanged"
                    @selectionChanged="onSelectionChanged"
                    ref="patientTable"
                >
                </MdTable>
            </b-col>
        </b-row>
    </BaseModal>
</template>

<script>
import Utils from '@/common/utils.js';
import PostPaymentService from '@/services/postPayment.service';
import ShowMessage from '@/components/messages/ShowMessage.js';
import DBFunctions from '@/common/DBFunctions.js';

export default {
    name: 'InsurancePatientSelectionModal',
    mixins: [ShowMessage, DBFunctions],
    props: {
        visible: {
            type: Boolean,
            required: true
        },
        insuranceInfo: {
            type: Object,
            required: true
        },
        dateTo: {
            type: String,
            default: null
        },
        dateFrom: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            modalId: `modal-${Utils.generateUUID()}`,
            emitCloseEvent: false,
            editableItem: [],
            items: [],

            parameters: {
                searchCriteria: '',
                dateFrom: this.dateFrom,
                dateTo: this.dateTo
            },
            searching: false,
            isDateFromValid: true,
            isDateToValid: true,
            dirInput: this.$i18n.locale === 'ar' ? 'rtl' : 'ltr',
            showMessageIsEmpty: false,
            totalRows: 0,
            pageOptions: [5, 10, 15],
            currentPage: 1,
            perPage: 5,
            fields: [
                {
                    key: 'patientName',
                    sortable: true,
                    label: this.$t('Shared.PatientName'),
                    class: 'text-left'
                },
                {
                    key: 'patientCode',
                    sortable: true,
                    label: this.$t('Shared.PatientCode'),
                    class: 'text-left',
                    thStyle: { width: '20%' }
                }
            ],
            selectedItems: []
        };
    },
    computed: {
        title() {
            return `${this.$t('PPDbContacts.SelectPatientTitle')}: ${
                this.insuranceInfo.description
            }`;
        },
        patientTable() {
            return this.$refs.patientTable;
        },
        saveButtonDisabled() {
            return this.selectedItems.length === 0;
        }
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.$bvModal.show(this.modalId);
                this.emitCloseEvent = true;
            }
        },
        dateFrom(newValue) {
            this.parameters.dateFrom = newValue;
        },
        dateTo(newValue) {
            this.parameters.dateTo = newValue;
        }
    },
    methods: {
        resetData() {
            this.editableItem = [];
            this.items = [];
            this.totalRows = 0;
            this.currentPage = 1;
            this.selectedItems = [];
            this.parameters.searchCriteria = '';
            this.parameters.dateFrom = this.dateFrom;
            this.parameters.dateTo = this.dateTo;
            this.showMessageIsEmpty = false;
        },

        emitEventCancel() {
            this.emitCloseEvent = false;
            this.resetData();
            this.$emit('update:visible', false);
        },

        emitEventOK(bvModalEvent) {
            if (this.validate()) {
                this.$emit('update:visible', false);
                this.$emit('ok', {
                    selectedItems: this.selectedItems,
                    dateFrom: this.parameters.dateFrom,
                    dateTo: this.parameters.dateTo
                });
                this.resetData();
            } else {
                bvModalEvent.preventDefault();
            }
        },

        onHidden() {
            if (this.emitCloseEvent) {
                this.$emit('update:visible', false);
                this.emitCloseEvent = false;
                this.resetData();
            }
        },

        close() {
            this.emitEventCancel();
        },

        validate() {
            return this.selectedItems.length > 0;
        },

        dateFromChanged(value) {
            this.parameters.dateFrom = value;
        },

        dateToChanged(value) {
            this.parameters.dateTo = value;
        },

        searchAsync() {
            this.patientTable.clearSelection();
            this.listingAsync();
        },

        validate_date() {
            if (this.parameters.dateFrom && this.parameters.dateTo) {
                const result = Utils.validateDateRange(
                    this.parameters.dateFrom,
                    this.parameters.dateTo
                );

                if (!result.valid) {
                    this.isDateToValid = !result.fields.includes('toDate');
                    this.isDateFromValid = !result.fields.includes('fromDate');
                    this.showMessage(this.$tc('Msg.Error'), this.$t(result.message), 'error');
                    return false;
                }

                this.isDateToValid = true;
                this.isDateFromValid = true;
            } else {
                this.isDateToValid =
                    !this.parameters.dateTo || Utils.isValidDate(this.parameters.dateTo);
                this.isDateFromValid =
                    !this.parameters.dateFrom || Utils.isValidDate(this.parameters.dateFrom);

                if (!this.isDateToValid || !this.isDateFromValid) {
                    this.showMessage(
                        this.$tc('Msg.Error'),
                        this.$t('Msg.CheckRedLabeledFields'),
                        'errorAutoHide'
                    );

                    return false;
                }
            }

            return true;
        },

        async listingAsync() {
            if (this.validate_date()) {
                this.searching = true;
                const parameters = {
                    ...this.parameters,
                    PageNumber: this.currentPage,
                    PageSize: this.perPage,
                    InsuranceCode: this.insuranceInfo.code
                };

                await PostPaymentService.getPatientPayment(parameters)
                    .then(response => {
                        if (response.status == '200' && response.data) {
                            this.items = response.data;

                            let headerString = response.headers['x-pagination'];
                            const header = JSON.parse(headerString);
                            this.totalRows = header.TotalCount;
                        }

                        this.showMessageIsEmpty = this.items.length === 0;
                        this.searching = false;
                    })
                    .catch(error => {
                        this.searching = false;
                        this.showResponseErrorMessage(error);
                    });
            }
        },

        async getAllPatientsForSelectAll(searchCriteria) {
            if (this.validate_date()) {
                const parameters = {
                    ...this.parameters,
                    searchCriteria:
                        searchCriteria !== undefined
                            ? searchCriteria
                            : this.parameters.searchCriteria,
                    InsuranceCode: this.insuranceInfo.code
                };
                const response = await PostPaymentService.getPatientPayment(parameters);
                if (response.status === 200 && response.data) {
                    return response.data;
                }
            }
            return [];
        },

        PerPageFunction(perpage) {
            this.perPage = perpage;
            this.currentPage = 1;
            this.listingAsync();
        },

        pageChanged(page) {
            this.currentPage = page;
            this.listingAsync();
        },

        onFiltered() {},

        onSelectionChanged(items) {
            this.selectedItems = items;
        },

        onSearchCriteriaChanged(criteria) {
            this.parameters.searchCriteria = criteria;
            this.searchAsync();
        }
    }
};
</script>
